import React from 'react';
import { motion } from 'framer-motion';
import { useParams, Link } from 'react-router-dom';
import ReactECharts from 'echarts-for-react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { toast } from 'sonner';

const { FiArrowLeft, FiMail, FiEye, FiMousePointer, FiTarget, FiUsers, FiTrendingUp, FiStar, FiExternalLink } = FiIcons;

const CampaignDetail = () => {
  const { id } = useParams();

  // Mock campaign data
  const campaign = {
    id: 'last',
    title: 'New Album Promo',
    type: 'Email Campaign',
    sentDate: '2024-05-15',
    subject: '🎵 My new album is here! Stream it now',
    recipients: 1847,
    delivered: 1812,
    opened: 761,
    clicked: 272,
    converted: 127,
    deliveryRate: 98.1,
    openRate: 42.0,
    clickRate: 15.0,
    conversionRate: 7.0
  };

  // Engagement over time chart
  const engagementOption = {
    tooltip: {
      trigger: 'axis',
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderColor: '#e2e8f0'
    },
    legend: {
      data: ['Opens', 'Clicks'],
      textStyle: { color: '#64748b' }
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: ['Day 1', 'Day 2', 'Day 3', 'Day 4', 'Day 5', 'Day 6', 'Day 7'],
      axisLabel: { color: '#64748b' }
    },
    yAxis: {
      type: 'value',
      axisLabel: { color: '#64748b' },
      splitLine: { lineStyle: { color: '#f1f5f9' } }
    },
    series: [
      {
        name: 'Opens',
        type: 'line',
        data: [456, 234, 156, 89, 67, 45, 23],
        smooth: true,
        lineStyle: { color: '#8b5cf6' },
        itemStyle: { color: '#8b5cf6' }
      },
      {
        name: 'Clicks',
        type: 'line',
        data: [167, 89, 56, 34, 23, 15, 8],
        smooth: true,
        lineStyle: { color: '#06b6d4' },
        itemStyle: { color: '#06b6d4' }
      }
    ]
  };

  const topLinks = [
    { name: 'Stream the New Album', clicks: 320, percentage: 45 },
    { name: 'Merch Store', clicks: 250, percentage: 35 },
    { name: 'Tour Dates', clicks: 142, percentage: 20 }
  ];

  const handleExportReport = () => {
    toast.success('Report exported successfully!', {
      description: 'Your campaign report has been downloaded to your device.'
    });
  };

  const handleDuplicateCampaign = () => {
    toast.success('Campaign duplicated!', {
      description: 'A copy of this campaign has been created and is ready to edit.'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link to="/analytics">
            <motion.button
              whileHover={{ scale: 1.05 }}
              className="p-2 text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
            >
              <SafeIcon icon={FiArrowLeft} className="text-xl" />
            </motion.button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-slate-800 dark:text-slate-200">{campaign.title}</h1>
            <p className="text-slate-600 dark:text-slate-400 mt-1">{campaign.type} • Sent on {campaign.sentDate}</p>
          </div>
        </div>
        <div className="flex space-x-3">
          <motion.button
            whileHover={{ scale: 1.05 }}
            onClick={handleExportReport}
            className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 px-4 py-2 rounded-lg font-medium hover:bg-slate-50 dark:hover:bg-slate-700"
          >
            Export Report
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            onClick={handleDuplicateCampaign}
            className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-2 rounded-lg font-medium"
          >
            Duplicate Campaign
          </motion.button>
        </div>
      </div>

      {/* Campaign Summary */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-slate-800 rounded-xl p-6 shadow-sm border border-slate-200 dark:border-slate-700"
      >
        <div className="flex items-center space-x-3 mb-4">
          <SafeIcon icon={FiMail} className="text-purple-600 text-xl" />
          <h2 className="text-xl font-bold text-slate-800 dark:text-slate-200">Campaign Summary</h2>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <SafeIcon icon={FiUsers} className="text-blue-600 text-2xl mx-auto mb-2" />
            <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{campaign.deliveryRate}%</p>
            <p className="text-sm text-slate-600 dark:text-slate-400">Delivery Rate</p>
            <p className="text-xs text-slate-500 dark:text-slate-500 mt-1">{campaign.delivered.toLocaleString()} delivered</p>
          </div>
          
          <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
            <SafeIcon icon={FiEye} className="text-green-600 text-2xl mx-auto mb-2" />
            <p className="text-2xl font-bold text-green-600 dark:text-green-400">{campaign.openRate}%</p>
            <p className="text-sm text-slate-600 dark:text-slate-400">Open Rate</p>
            <p className="text-xs text-slate-500 dark:text-slate-500 mt-1">{campaign.opened.toLocaleString()} opens</p>
          </div>
          
          <div className="text-center p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
            <SafeIcon icon={FiMousePointer} className="text-purple-600 text-2xl mx-auto mb-2" />
            <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">{campaign.clickRate}%</p>
            <p className="text-sm text-slate-600 dark:text-slate-400">Click Rate</p>
            <p className="text-xs text-slate-500 dark:text-slate-500 mt-1">{campaign.clicked.toLocaleString()} clicks</p>
          </div>
          
          <div className="text-center p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
            <SafeIcon icon={FiTarget} className="text-orange-600 text-2xl mx-auto mb-2" />
            <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">{campaign.conversionRate}%</p>
            <p className="text-sm text-slate-600 dark:text-slate-400">Conversion Rate</p>
            <p className="text-xs text-slate-500 dark:text-slate-500 mt-1">{campaign.converted.toLocaleString()} conversions</p>
          </div>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Engagement Over Time */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white dark:bg-slate-800 rounded-xl p-6 shadow-sm border border-slate-200 dark:border-slate-700"
        >
          <h2 className="text-xl font-bold text-slate-800 dark:text-slate-200 mb-6">Opens & Clicks Over Time</h2>
          <ReactECharts option={engagementOption} style={{ height: '300px' }} />
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-4 text-center">
            Campaign engagement tracked over 7 days
          </p>
        </motion.div>

        {/* Top Links & AI Insights */}
        <div className="space-y-6">
          {/* Top Links */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white dark:bg-slate-800 rounded-xl p-6 shadow-sm border border-slate-200 dark:border-slate-700"
          >
            <h2 className="text-xl font-bold text-slate-800 dark:text-slate-200 mb-6">Top Links</h2>
            <div className="space-y-4">
              {topLinks.map((link, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-700 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full flex items-center justify-center text-white text-sm font-bold">
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-medium text-slate-800 dark:text-slate-200">{link.name}</p>
                      <p className="text-sm text-slate-500 dark:text-slate-400">{link.clicks} clicks</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="w-16 bg-slate-200 dark:bg-slate-600 rounded-full h-2">
                      <div 
                        className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full"
                        style={{ width: `${link.percentage}%` }}
                      ></div>
                    </div>
                    <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">{link.percentage}%</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* AI Insights */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl p-6 text-white"
          >
            <div className="flex items-center space-x-3 mb-4">
              <SafeIcon icon={FiStar} className="text-white text-xl" />
              <h2 className="text-xl font-bold">AI Insights</h2>
            </div>
            <div className="space-y-4">
              <div className="bg-white/10 rounded-lg p-4">
                <h3 className="font-semibold mb-2">🎯 Performance Highlight</h3>
                <p className="text-white/90 text-sm">
                  This campaign had the highest click rate among casual fans in the past 6 months (15% vs 9% average).
                </p>
              </div>
              <div className="bg-white/10 rounded-lg p-4">
                <h3 className="font-semibold mb-2">📈 Optimization Tip</h3>
                <p className="text-white/90 text-sm">
                  Album streaming links performed 3x better than merch. Consider leading with music content in future campaigns.
                </p>
              </div>
              <div className="bg-white/10 rounded-lg p-4">
                <h3 className="font-semibold mb-2">🔄 Next Steps</h3>
                <p className="text-white/90 text-sm">
                  Send a follow-up campaign to the 634 fans who opened but didn't click within 48 hours.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Campaign Details */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-slate-800 rounded-xl p-6 shadow-sm border border-slate-200 dark:border-slate-700"
      >
        <h2 className="text-xl font-bold text-slate-800 dark:text-slate-200 mb-6">Campaign Details</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="font-semibold text-slate-800 dark:text-slate-200 mb-3">Message Content</h3>
            <div className="bg-slate-50 dark:bg-slate-700 rounded-lg p-4">
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-2">Subject Line:</p>
              <p className="font-medium text-slate-800 dark:text-slate-200 mb-4">{campaign.subject}</p>
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-2">Message:</p>
              <p className="text-slate-700 dark:text-slate-300">
                Hey there! 🎵 I'm so excited to share my brand new album with you! After months of writing, recording, and perfecting every note, it's finally here and ready for your ears. 
                <br /><br />
                Stream it now on all platforms and let me know which track is your favorite! Your support means everything to me. ❤️
              </p>
            </div>
          </div>
          
          <div>
            <h3 className="font-semibold text-slate-800 dark:text-slate-200 mb-3">Campaign Stats</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center p-3 bg-slate-50 dark:bg-slate-700 rounded-lg">
                <span className="text-slate-600 dark:text-slate-400">Recipients</span>
                <span className="font-semibold text-slate-800 dark:text-slate-200">{campaign.recipients.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-slate-50 dark:bg-slate-700 rounded-lg">
                <span className="text-slate-600 dark:text-slate-400">Send Date</span>
                <span className="font-semibold text-slate-800 dark:text-slate-200">{campaign.sentDate}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-slate-50 dark:bg-slate-700 rounded-lg">
                <span className="text-slate-600 dark:text-slate-400">Campaign Type</span>
                <span className="font-semibold text-slate-800 dark:text-slate-200">{campaign.type}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <span className="text-green-700 dark:text-green-400">Revenue Generated</span>
                <span className="font-bold text-green-600 dark:text-green-400">$3,247</span>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default CampaignDetail;